/* Варианты «Создать промпт» и «Улучшить промпт» на месте, без страницы заказа (§7.3): заголовок «Варианты» и что выбрано
   («Для фото · Кино»), карточки с подписью сервера (нет подписи — «Вариант N»), «Скопировать» и «Использовать в Фото / Видео»
   (у музыки и текста в «Создать» — «Улучшить»: текст уходит в «Улучшить промпт»); отказ и сбой — заметкой на месте. */
import { useRef } from 'react';
import type { SpeechOrder } from '../../../api/speech';
import { uiFormat, uiText } from '../../../i18n';
import { cNum, tailError, tailReceipt } from '../../studio/circle/order-tail-model';
import { StudioIcon } from '../../studio/StudioIcon';
import { openStudioTarget } from '../../studio/studio-navigation';
import { clipText, forWords } from './text-model';
import { CopyButton } from './TextReading';
import { useTextCopy } from './use-text-copy';
import { useTextResult } from './use-text-result';
import { PROMPT_MAX } from '../../studio/prompt-limit';

export interface VariantsSnapshot {
  kind: 'pnew' | 'pfix';
  target: string;
  templates: string[];
  own: boolean;
}
function variantsMeta(snapshot: VariantsSnapshot) {
  return [
    forWords[snapshot.target] ? uiFormat('Для {0}', uiText(forWords[snapshot.target])) : '',
    snapshot.templates.map(item => uiText(item)).join(', '),
    snapshot.own ? uiText('свой вариант') : '',
  ]
    .filter(Boolean)
    .join(' · ');
}
function Skeleton({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div className="tt-var sk" key={index} style={{ ['--d' as string]: `${(index * 0.14).toFixed(2)}s` }}>
          <b />
          <i />
          <i />
          <i />
        </div>
      ))}
    </>
  );
}
function Group({ order, owner, snapshot, copier }: { order: SpeechOrder; owner?: number; snapshot: VariantsSnapshot; copier: ReturnType<typeof useTextCopy> }) {
  const result = useTextResult(order, owner);
  const nodes = useRef<Record<string, HTMLElement | null>>({});
  if (order.status === 'failed') {
    const error = tailError(order);
    const amount = tailReceipt(order)?.amount ?? null;
    return error?.kind === 'moderation' ? (
      <p className="note bad" role="alert">
        {uiText('Не прошло проверку: политику и запрещённые темы не пропускаем.')}
        {amount !== null && ` ${uiFormat('{0} C списаны', cNum(amount))}`}
      </p>
    ) : (
      <p className="note warn" role="alert">
        {uiText('Сбой на нашей стороне')}
        {amount !== null ? `, ${uiFormat('{0} C вернулись на баланс', cNum(amount))}` : ''}. {uiText('Попробуйте ещё раз')}
      </p>
    );
  }
  if (order.status !== 'done') return <Skeleton count={snapshot.target === 'photo' || snapshot.target === 'video' ? 3 : 2} />;
  const document = result.document;
  if (!document)
    return (
      <p className="hintp" role={result.error ? 'alert' : 'status'}>
        {uiText(result.error ? 'Не удалось открыть варианты.' : 'Загружаем…')}{' '}
        {result.error && (
          <button type="button" className="lnk" onClick={result.retry}>
            {uiText('Повторить')}
          </button>
        )}
      </p>
    );
  const texts = document.variants.length ? document.variants : [document.transcript].filter(Boolean);
  const media = snapshot.target === 'photo' || snapshot.target === 'video';
  return (
    <>
      {texts.map((text, index) => {
        const id = `${order.id}:${index}`;
        return (
          <div className="tt-rd tt-var" key={id} ref={node => void (nodes.current[id] = node)}>
            <div className="tt-rh">
              <span className="m">
                <b>{document.titles[index] || uiFormat('Вариант {0}', index + 1)}</b>
              </span>
            </div>
            <p className="tt-p">{text}</p>
            <div className="tt-va">
              <CopyButton id={id} text={text} node={() => nodes.current[id] ?? null} copier={copier} />
              {media ? (
                <button
                  className="tt-cp"
                  type="button"
                  onClick={() =>
                    openStudioTarget({
                      screen: snapshot.target,
                      options: { mode: snapshot.target === 'photo' ? 'generate' : 'text', description: clipText(text, PROMPT_MAX) },
                    })
                  }
                >
                  <StudioIcon kind={snapshot.target === 'photo' ? 'image' : 'video'} />
                  <span>{uiText(snapshot.target === 'photo' ? 'Использовать в Фото' : 'Использовать в Видео')}</span>
                </button>
              ) : (
                snapshot.kind === 'pnew' && (
                  <button
                    className="tt-cp"
                    type="button"
                    onClick={() => openStudioTarget({ screen: 'text', options: { mode: 'improve', text, kind: snapshot.target } })}
                  >
                    <StudioIcon kind="sparkle" />
                    <span>{uiText('Улучшить')}</span>
                  </button>
                )
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
export function TextVariants({ orders, owner, snapshot }: { orders: SpeechOrder[]; owner?: number; snapshot: VariantsSnapshot }) {
  const copier = useTextCopy();
  if (!orders.length) return null;
  const running = orders.some(order => !['done', 'failed', 'uncertain'].includes(order.status));
  return (
    <div className="tt-w" id="ttVarsW">
      <div className="tt-vars" id="ttVars" aria-busy={running || undefined} aria-live="polite">
        <div className="tt-vh">
          <b>{uiText(running && orders.length === 1 ? 'Пишем варианты' : 'Варианты')}</b>
          <small>{variantsMeta(snapshot)}</small>
        </div>
        {orders.map(order => (
          <Group key={order.id} order={order} owner={owner} snapshot={snapshot} copier={copier} />
        ))}
        {orders.some(order => order.status === 'uncertain') && (
          <p className="hintp" role="status">
            {uiText('Результат пока не подтверждён. Проверьте этот заказ, прежде чем создавать новый.')}
          </p>
        )}
        {copier.failed && (
          <p className="hintp" role="status">
            {uiText('Текст выделен: скопируйте его вручную')}
          </p>
        )}
      </div>
    </div>
  );
}
